/**
 * Route guard that blocks direct URL access and deeplinks into Courses
 * (First Steps / Next Steps) for pastoral status N (New Friend) and V
 * (Visitor). The First Steps entry button on AllPage is CMS-driven
 * (minPastoralRole) — this guard is the frontend enforcement point for
 * navigation that bypasses that button entirely (typed URLs, QR deeplinks).
 *
 * Sort values come from GET /profile/pastoral-mapping — V=1, N=2, G=3, I=4,
 * GI=4.5, R=5, then leader roles (PTL, CGL, TL, ZM, ...) at 6-17. G=3 is the
 * cutoff, so this blocks exactly N and V while every leader role (well above
 * G) still passes.
 *
 * Fails closed: if the pastoral sort can't be resolved (profile/mapping
 * unavailable, unknown status), Courses is blocked — same precedent as the
 * fail-closed leader-access check (checkLeaderAccess in
 * store/modules/courses.js).
 *
 * The sort is the FIRST question, never the only one. Below the cutoff there are
 * three more, one per route shape, and STATIC_SEGMENTS below says which applies:
 * a program asks for an active entitlement, the notes surface asks the server's
 * account-level note capability, and the exempt routes ask nothing. Every one of
 * those exists because the plain sort gate had already bounced somebody the
 * feature was built for.
 */
import { isNsMockEnabled, getMockRoleSort, buildAccess } from '@/utils/next-steps-mock';

// The account-level note capability, as the server states it: any program whose
// `features.noteHub` is on. The same rule as the `courses/canUseNoteFeatures`
// getter, applied to a raw access summary so the mock branch below can ask it of
// a synthetic one without going through the store.
function grantsNoteFeatures(access) {
  return (access?.programs || []).some(p => p?.features?.noteHub);
}

// Can this member get into Next Steps at all — any program they may see or already
// hold. The same rule as the `courses/canEnterCourses` getter, against a raw
// summary so the mock branch can ask it of a synthetic one.
function canEnterCourses(access) {
  return (access?.programs || []).some(p => p?.visible || p?.tier !== 'PREVIEW');
}

export const COURSES_MIN_PASTORAL_SORT = 3;

// The chooser at bare /courses is where a V/N who bought a book at the IDR
// counter starts — the part cards tell them which program their card unlocks,
// and `/courses/redeem/:code` (its own guard, exempt for the same reason) is the
// lane that actually redeems it. Opening the chooser grants nothing and reveals
// nothing they couldn't already see on the All page: the part cards below it,
// and every program landing under /courses/:programSlug, stay entitlement-gated
// exactly as before.
//
function isChooserRoute(slug) {
  return !slug;
}

// EVERY static segment under /courses, and what the status gate should do with it.
//
// The first path segment is not always a program. `/courses/resource/NSL5-1` reads
// as a program called "resource", and an entitlement check for a program that does
// not exist can only fail — which is how a V or N ended up bounced Home from a
// scanned booklet QR before the page ever ran.
//
// Listed EXHAUSTIVELY, and asserted against the router, so adding a static route
// under /courses is a decision rather than an accident. The failure mode is silent:
// a new segment inherits "treat as a program", the check fails, and the reader is
// sent Home with nothing to read.
//
//   exempt       reachable regardless of pastoral status
//   note-capable status gate first, then the ACCOUNT-level note capability
//   own-guard    never reaches this guard at all; listed so the set stays exhaustive
//
const STATIC_SEGMENTS = {
  // A scanned booklet QR. The SERVER decides what the reader gets, and one of its
  // answers is a PUBLIC edition served precisely to someone below this cutoff.
  // Gating the door on the status the answer is FOR means the people it is for
  // never reach it — the same reasoning as the chooser above. Grants nothing: the
  // page immediately asks the server, which applies the real gate.
  resource: 'exempt',
  // The refusal screen itself. Bouncing someone off the page that exists to explain
  // why they were bounced leaves them with nothing but a silent trip Home. Static
  // copy; grants nothing.
  'leader-only': 'exempt',
  // A member feature, not a way in. It is NOT a program, so the per-program
  // entitlement fallback below must not run for it — there is no entitlement to a
  // notes hub. The ACCOUNT-level capability is the question that means something
  // here, and it is asked instead.
  //
  // This used to be a plain sort gate, on the reasoning that anybody who can reach
  // the Hub holds notes already, which needed a tier far above this cutoff. That
  // stopped being true twice over: highlights, notes and the hub are now on at
  // EVERY tier (`resolveFeatures` in the API — a free-in-full Lesson 1 that cannot
  // be marked up is not the product being sampled), and `courses-nv-visibility`
  // lets an N or V open that lesson. So a V could highlight Lesson 1, take notes in
  // it, see the Hub icon in the reader header — which shows on `canUseNoteFeatures`,
  // and it is true for them — tap it, and land back on the All page. Their notes
  // existed, the API served them, and the only thing between the two was this line.
  notes: 'note-capable',
  // `/courses/devices/*` — the encryption screens: the Devices list, "Approve this
  // device", and the recovery/reset screens. Exactly the same shape as `notes`
  // above and classified with it, for the same reason plus one of their own: the
  // Hub REDIRECTS here when this device holds no key (`redirectIfAnnotationsLocked`),
  // so a status that can reach the Hub but not these screens is worse than one that
  // can reach neither — it is a member sent Home from the one screen that could
  // have given them their notes back.
  devices: 'note-capable',
  // `/courses/redeem/:code` carries its OWN beforeEnter and never reaches this
  // guard — deliberately, for the reason `resource` is exempt here: a V or N
  // holding a printed card is exactly who that lane exists for. Listed so the set
  // stays exhaustive against the router; the value is a note, not a behaviour.
  redeem: 'own-guard',
  // `/courses/unlock/:token` is the leader unlock QR's landing page, and it is the
  // same shape as `redeem` above for the same reason: it carries its own
  // beforeEnter (auth only) and never reaches this guard. A member arriving with a
  // code their leader just handed them is precisely who that lane exists for, so
  // bouncing them on pastoral sort would defeat it. Listed so the set stays
  // exhaustive against the router; the value is a note, not a behaviour.
  unlock: 'own-guard',
};

/** Is this first segment a static route rather than a program slug? */
function isStaticSegment(slug) {
  return Object.prototype.hasOwnProperty.call(STATIC_SEGMENTS, slug);
}

// Entitlement-aware allowance: a V/N below the status cutoff may still enter a
// program they hold an ACTIVE entitlement for (e.g. they bought the physical
// book in person and unlocked it with the book code). "Active" = a granted tier
// above PREVIEW, or the server marking the program visible. Only relevant when
// the route targets a specific program (`:programSlug`); the notes surface asks
// `grantsNoteFeatures` instead, which is account-level rather than per program.
function hasActiveProgramGrant(store, slug) {
  if (!slug) return false;
  const visible = store.getters['courses/programVisible']?.(slug);
  const tier = store.getters['courses/programTier']?.(slug);
  return Boolean(visible) || (typeof tier === 'string' && tier !== 'PREVIEW');
}

// The program slug for the route. Most Courses routes carry it as `:programSlug`,
// but some static routes under `/courses/<slug>/…` have none — so fall back to the
// first path segment after `/courses/` (e.g. `next-steps-p1`). Without this, an
// entitled V/N on such a route would be wrongly redirected home (the entitlement
// branch would see no slug).
function programSlugForRoute(to) {
  const param = to?.params?.programSlug;
  if (param) return param;
  const afterCourses = String(to?.path || '').split('/courses/')[1];
  return afterCourses ? afterCourses.split('/')[0] : undefined;
}

export function createCoursesPastoralGuard(store, fallbackPath = '/tabs/home') {
  return async to => {
    const slug = programSlugForRoute(to);

    // Checked before the mock branch and before any network call: reaching these
    // must not depend on resolving a pastoral status.
    if (isChooserRoute(slug) || STATIC_SEGMENTS[slug] === 'exempt') return;

    // DEV-ONLY: Next Steps Mock Mode drives the pastoral-status threshold from
    // the local mock state instead of the real profile/mapping lookup, so the
    // PM console can preview every status without a matching real account.
    // Inert unless the mock toggle is on AND this isn't a production build.
    if (isNsMockEnabled() && process.env.VUE_APP_NODE_ENV !== 'production') {
      const mockSort = getMockRoleSort();
      if (typeof mockSort === 'number' && mockSort >= COURSES_MIN_PASTORAL_SORT) {
        return;
      }
      // Below the cutoff: the notes surface asks the account-level capability, a
      // program asks for an active tier. Same split as the real branch below, and
      // the same precondition — a status the console has not resolved is not a
      // below-cutoff status, so it decides nothing here either.
      const mockAccess = buildAccess();
      if (STATIC_SEGMENTS[slug] === 'note-capable') {
        // The console models a role and its programs, not a vault — there is no
        // mock `hasAnnotations`, so the "already wrote something" reprieve of the
        // real branch cannot apply here. What it CAN preview is the half that
        // matters for setting the switch: at a below-cutoff role, the Hub follows
        // program visibility.
        const capable =
          typeof mockSort === 'number' &&
          grantsNoteFeatures(mockAccess) &&
          canEnterCourses(mockAccess);
        return capable ? undefined : fallbackPath;
      }
      const program = mockAccess.programs.find(p => p.slug === slug);
      if (program && (program.visible || program.tier !== 'PREVIEW')) {
        return;
      }
      return fallbackPath;
    }

    try {
      await Promise.all([
        store.dispatch('profile/fetchProfile'),
        store.dispatch('lookups/fetchPastoralMapping'),
      ]);
    } catch (error) {
      console.warn('[Courses] Failed to resolve pastoral status for route guard:', error?.message);
    }

    const roleId = store.getters['profile/pastoralRoleId'];
    const sort = store.state.lookups.pastoralMapping?.[roleId];

    if (typeof sort === 'number' && sort >= COURSES_MIN_PASTORAL_SORT) {
      return;
    }

    // Below the status cutoff (V/N), and on the notes surface: the question is not
    // which program they hold but whether the SERVER grants them notes at all —
    // the Hub spans programs, so no single program's entitlement answers for it.
    // That is `features.noteHub`, the same field the reader's Hub icon reads, and
    // the same one `CourseFeaturesGuard` enforces on every note write. Deciding it
    // here on pastoral sort instead meant the client refused what the API allowed.
    //
    // A RESOLVED status is the precondition. `sort` is unresolved when the profile
    // or mapping call failed, or the role is not in the mapping at all, and the
    // fail-closed contract at the top of this file covers exactly that case: an
    // unresolved status is not a below-cutoff status, and must not be treated as
    // one. Without this, a partial outage would admit anyone the access summary
    // happened to answer for — deciding on one lookup because the other broke.
    //
    // Beyond it, `fetchCourseAccess` commits null on any failure, so an unreachable
    // API leaves no programs and no capability. Wrapped anyway — a rejection out of
    // a beforeEnter aborts the navigation with an error instead of redirecting,
    // which would strand the member on the page they came from.
    // And the capability is not the whole answer for someone below the cutoff.
    // `features.noteHub` is on at every tier, so on its own it would hold the Hub
    // open for an N/V for ever — including after `courses-nv-visibility`, the
    // launch-weekend switch, has closed Courses to them again. Taking the programs
    // back is the point of that switch, so the Hub goes with them: `canEnterCourses`
    // is false once nothing is visible and nothing is held.
    //
    // UNLESS they have already written something. A member who highlighted a lesson
    // and took notes on it during the launch weekend still owns that work, we still
    // store it, and the API still serves it — closing the only door to it would be
    // taking away what they made, not what we lent them. `hasAnnotations` is the
    // server's own answer to "is there anything behind this door", asked on the
    // summary this guard already fetches. It reopens on its own if the flag comes
    // back, if they buy the book, or if they reach G.
    if (STATIC_SEGMENTS[slug] === 'note-capable' && typeof sort === 'number') {
      try {
        await store.dispatch('courses/fetchCourseAccess');
        const keepsTheirWork = store.getters['courses/hasAnnotations'];
        const stillInCourses = store.getters['courses/canEnterCourses'];
        if (store.getters['courses/canUseNoteFeatures'] && (stillInCourses || keepsTheirWork)) {
          return;
        }
      } catch (error) {
        console.warn('[Courses] Failed to resolve note capability for guard:', error?.message);
      }
      return fallbackPath;
    }

    // Below the status cutoff (V/N): still allow into a specific program they
    // hold an active entitlement for. Refresh the access summary first so a
    // just-redeemed book code is reflected. Fails closed on any error.
    //
    // A PROGRAM only. Asking whether someone holds an entitlement to `notes` is a
    // question with no answer, and the false it returns is right by accident — the
    // same accident that sent a V Home from `resource` before it was classified.
    // Skipping it here means the fallback runs only where it means something, and a
    // new static segment cannot inherit a lookup that was never about it.
    if (slug && !isStaticSegment(slug)) {
      try {
        await store.dispatch('courses/fetchCourseAccess');
        if (hasActiveProgramGrant(store, slug)) {
          return;
        }
      } catch (error) {
        console.warn('[Courses] Failed to resolve course entitlement for guard:', error?.message);
      }
    }

    return fallbackPath;
  };
}
